import React, { useState, useEffect } from 'react';
import { X, Upload, Send, Loader, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ELIGIBLE_DOMAINS = ['microsoft.com', 'technofocus.co', 'technofocus.in', 'technofocus.ai'];

const isEligibleUser = (email) => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return ELIGIBLE_DOMAINS.includes(domain);
};

const STATUS_COLORS = {
  pending:  { bg: '#F59E0B', hover: '#D97706', badge: '#FEF3C7', badgeText: '#92400E' },
  rejected: { bg: '#EF4444', hover: '#DC2626', badge: '#FEE2E2', badgeText: '#991B1B' },
  active:   { bg: '#059669', hover: '#047857', badge: '#D1FAE5', badgeText: '#065F46' },
  none:     { bg: '#0F4C75', hover: '#0A3A5C', badge: null,      badgeText: null },
};

const RequestUploadAccessWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [team, setTeam] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 'loading' | 'none' | 'pending' | 'rejected'
  const [requestStatus, setRequestStatus] = useState('loading');
  const [requestData, setRequestData] = useState(null);

  // Fetch current request status on mount
  useEffect(() => {
    if (!user?.email || !isEligibleUser(user.email)) return;
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/my/upload-access-request`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.request) {
            setRequestData(data.request);
            setRequestStatus(data.request.status === 'approved' ? 'approved' : data.request.status);
          } else {
            setRequestStatus('none');
          }
        } else {
          setRequestStatus('none');
        }
      } catch {
        setRequestStatus('none');
      }
    };
    fetchStatus();
  }, [user]);

  if (!user?.email || !isEligibleUser(user.email)) return null;

  // Don't show for admin users — they don't need it
  const roles = user.roles || [];
  if (roles.includes('admin')) return null;

  // Still loading — don't render yet to avoid flicker
  if (requestStatus === 'loading') return null;

  // If user already has content_owner role and their request is approved, show 'active' state
  const effectiveStatus = (roles.includes('content_owner') && requestStatus !== 'pending' && requestStatus !== 'rejected')
    ? 'active'
    : requestStatus;

  const colors = STATUS_COLORS[effectiveStatus] || STATUS_COLORS.none;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason for your request');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload-access-requests`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ team: team.trim(), reason: reason.trim() }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setRequestStatus('pending');
        setRequestData(data.request);
        setIsOpen(false);
        toast.success('Upload access request submitted! You will be notified by email.');
      } else if (res.status === 409) {
        setRequestStatus('pending');
        setIsOpen(false);
        toast.info('You already have a pending upload access request.');
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResubmit = () => {
    setTeam('');
    setReason('');
    setStatusOpen(false);
    setIsOpen(true);
  };

  const displayName = user.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user.name || user.email;

  const buttonLabel =
    effectiveStatus === 'pending'  ? 'Request Pending Review' :
    effectiveStatus === 'rejected' ? 'Request Declined' :
    effectiveStatus === 'active'   ? 'Upload Access Active' :
    'Request Upload Access';

  const ButtonIcon =
    effectiveStatus === 'pending'  ? Clock :
    effectiveStatus === 'rejected' ? XCircle :
    effectiveStatus === 'active'   ? CheckCircle :
    Upload;

  const isClickable = effectiveStatus === 'none' || effectiveStatus === 'rejected' || effectiveStatus === 'pending' || effectiveStatus === 'active';

  return (
    <>
      {/* Floating button — bottom-left */}
      <button
        onClick={() => {
          if (effectiveStatus === 'none') { setIsOpen(true); return; }
          setStatusOpen(s => !s);
        }}
        data-testid="upload-access-btn"
        title={buttonLabel}
        style={{
          position: 'fixed',
          bottom: '28px',
          left: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 18px',
          backgroundColor: colors.bg,
          color: 'white',
          border: 'none',
          borderRadius: '9999px',
          fontSize: '13px',
          fontWeight: '600',
          cursor: isClickable || requestStatus === 'pending' ? 'pointer' : 'default',
          boxShadow: `0 4px 14px ${colors.bg}55`,
          zIndex: 999,
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.hover || colors.bg; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.bg; }}
      >
        <ButtonIcon size={15} />
        {buttonLabel}
      </button>

      {/* Status popover — for pending / rejected states */}
      {statusOpen && (effectiveStatus === 'pending' || effectiveStatus === 'rejected' || effectiveStatus === 'active') && (
        <>
          <div
            onClick={() => setStatusOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
          />
          <div
            data-testid="upload-access-status-panel"
            style={{
              position: 'fixed',
              bottom: '78px',
              left: '24px',
              width: '300px',
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
              zIndex: 1041,
              overflow: 'hidden',
              border: '1px solid #E5E7EB',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: effectiveStatus === 'pending' ? '#FFFBEB' : effectiveStatus === 'active' ? '#ECFDF5' : '#FFF5F5',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {effectiveStatus === 'pending'
                  ? <Clock size={16} color="#D97706" />
                  : effectiveStatus === 'active'
                    ? <CheckCircle size={16} color="#059669" />
                    : <XCircle size={16} color="#EF4444" />
                }
                <span style={{ fontWeight: '700', fontSize: '14px', color: '#1F2937' }}>
                  {effectiveStatus === 'pending' ? 'Request Pending Review' :
                   effectiveStatus === 'active'  ? 'Upload Access Active' :
                   'Request Not Approved'}
                </span>
              </div>
              <button
                onClick={() => setStatusOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#9CA3AF' }}
                data-testid="upload-status-close-btn"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '14px 16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: '9999px',
                  fontSize: '12px', fontWeight: '600',
                  backgroundColor: colors.badge,
                  color: colors.badgeText,
                }}>
                  {effectiveStatus === 'pending' ? 'Pending' : effectiveStatus === 'active' ? 'Active' : 'Not Approved'}
                </span>
              </div>

              {requestData?.requested_at && effectiveStatus !== 'active' && (
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#6B7280' }}>
                  Submitted: {new Date(requestData.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              )}
              {requestData?.user_team && effectiveStatus !== 'active' && (
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#6B7280' }}>
                  Team: {requestData.user_team}
                </p>
              )}

              {effectiveStatus === 'active' && (
                <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#5A6C7D', lineHeight: 1.4 }}>
                  You have <strong>Content Owner</strong> access. You can upload and manage compete content assets on this portal.
                </p>
              )}

              {effectiveStatus === 'pending' && (
                <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#5A6C7D', lineHeight: 1.4 }}>
                  Your request is being reviewed. You will receive an email notification once a decision is made.
                </p>
              )}

              {effectiveStatus === 'rejected' && (
                <>
                  <p style={{ margin: '10px 0 12px 0', fontSize: '13px', color: '#5A6C7D', lineHeight: 1.4 }}>
                    Your request was not approved. You may submit a new request with additional details.
                  </p>
                  <button
                    onClick={handleResubmit}
                    data-testid="upload-access-resubmit-btn"
                    style={{
                      width: '100%',
                      padding: '9px 16px',
                      backgroundColor: '#0F4C75',
                      color: 'white',
                      border: 'none',
                      borderRadius: '7px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <RefreshCw size={14} /> Submit a New Request
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Request modal — shown for 'none' state or after resubmit */}
      {isOpen && effectiveStatus !== 'active' && (
        <div
          onClick={() => setIsOpen(false)}
          data-testid="upload-access-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="upload-access-modal"
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              backgroundColor: '#0F4C75',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Upload size={20} />
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Request Upload Access</h2>
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', opacity: 0.85, lineHeight: 1.4 }}>
                  You are requesting access to upload Microsoft compete content assets to this platform.
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                data-testid="upload-access-close-btn"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '6px',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '12px',
                  flexShrink: 0,
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Name & Email — pre-filled, read-only */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input
                    value={displayName}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: '#F9FAFB', cursor: 'default', color: '#6B7280' }}
                    data-testid="upload-access-name"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    value={user.email || ''}
                    readOnly
                    style={{ ...inputStyle, backgroundColor: '#F9FAFB', cursor: 'default', color: '#6B7280' }}
                    data-testid="upload-access-email"
                  />
                </div>
              </div>

              {/* Team / Role */}
              <div>
                <label style={labelStyle}>
                  Team / Role <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span>
                </label>
                <input
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  placeholder="e.g. Surface Compete, MAICPP, Marketing"
                  style={inputStyle}
                  data-testid="upload-access-team"
                />
              </div>

              {/* Reason */}
              <div>
                <label style={labelStyle}>Reason for requesting access *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Briefly describe why you need upload access and what content you plan to upload..."
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', fontFamily: 'inherit', lineHeight: 1.5 }}
                  data-testid="upload-access-reason"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !reason.trim()}
                data-testid="upload-access-submit-btn"
                style={{
                  width: '100%',
                  padding: '13px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: (isSubmitting || !reason.trim()) ? '#D1D5DB' : '#0F4C75',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: (isSubmitting || !reason.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s ease',
                }}
              >
                {isSubmitting ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>

              <p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                Your request will be reviewed by the Technofocus team. You will receive an email notification once a decision is made.
              </p>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '7px',
  border: '1px solid #D1D5DB',
  fontSize: '14px',
  color: '#1F2937',
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 0.15s ease',
};

export default RequestUploadAccessWidget;
